import React, { Component } from 'react';
import { CircularProgress } from '@material-ui/core';
import { Helmet } from 'react-helmet';
import Navbar from '../Navbar/Navbar';
import Footer from '../Footer/Footer';
import LoginModal from '../LoginModal/LoginModal';
import { auth } from '../AuthService';
import constants from '../Constants/constants';
import LazyImage from '../LazyImage/LazyImage';
import './Decks.css';

class DeckViewer extends Component {
  state = {
    deck: [],
    name: "",
    loading: true,
    error: null,
    isLoggedIn: auth.loggedIn(),
    showLoginModal: false,
    copyStatus: null, // null | "copying" | "copied" | "error"
  };

  componentDidMount() {
    const { id } = this.props.params;
    const endpoint = `${constants.DOMAIN}api/v1/guest/deck/${id}`;

    fetch(endpoint, {
      mode: 'cors',
      method: 'GET',
    })
      .then(async (res) => {
        if (!res.ok) throw new Error('Failed to fetch deck');
        const deck = await res.json();
        this.setState({ deck: deck.deck, loading: false, name: deck.name, deckOwnerId: deck.user?._id || deck.user });
      })
      .catch((err) => {
        this.setState({ error: err.message, loading: false });
      });

    window.addEventListener("authChange", this.handleAuthChange);
  }

  componentWillUnmount() {
    window.removeEventListener("authChange", this.handleAuthChange);
  }

  handleAuthChange = () => {
    this.setState({ isLoggedIn: auth.loggedIn() });
  }

  handleCopyDeck = () => {
    if (!this.state.isLoggedIn) {
      this.setState({ showLoginModal: true });
      return;
    }
    this.performCopy();
  }

  performCopy = () => {
    const { id } = this.props.params;
    this.setState({ copyStatus: "copying" });

    auth.authFetch(`${constants.DOMAIN}api/v1/decks/${id}/copy`, {
      method: 'POST',
    })
      .then((res) => res.json())
      .then((newDeck) => {
        this.setState({ copyStatus: "copied" });
        setTimeout(() => {
          this.setState({ copyStatus: null });
        }, 2000);
      })
      .catch(() => {
        this.setState({ copyStatus: "error" });
        setTimeout(() => {
          this.setState({ copyStatus: null });
        }, 2000);
      });
  }

  handleLoginSuccess = (userData) => {
    window.dispatchEvent(new Event("authChange"));
    this.setState({ showLoginModal: false, isLoggedIn: true }, () => {
      const currentUserId = auth.getUser()?._id;
      if (this.state.deckOwnerId && currentUserId === this.state.deckOwnerId) {
        return; // It's their own deck, skip copy
      }
      this.performCopy();
    });
  }

  sortByTurnCount(cards) {
    return cards.sort((a, b) => a.turnCount - b.turnCount);
  }

  categorizeCards(cards) {
    const categories = {
      characters: [],
      strategies: [],
      abilities: [],
      fieldCard: [],
    };

    cards.forEach((card) => {
      switch (card.cardType) {
        case 'Character':
          categories.characters.push(card);
          break;
        case 'Strategy':
          categories.strategies.push(card);
          break;
        case 'Ability':
          categories.abilities.push(card);
          break;
        case 'Field':
          categories.fieldCard.push(card);
          break;
        default:
          break;
      }
    });

    return categories;
  }

  getCopyButtonText() {
    switch (this.state.copyStatus) {
      case "copying": return "Copying...";
      case "copied": return "Copied!";
      case "error": return "Error";
      default: return this.state.isLoggedIn ? "Copy Deck" : "Login to Copy";
    }
  }

  render() {
    const { deck, loading, error, name, copyStatus, showLoginModal, isLoggedIn, deckOwnerId } = this.state;

    if (error) return <p>Error: {error}</p>;

    const { characters, strategies, abilities, fieldCard } = this.categorizeCards(deck);
    const currentUserId = isLoggedIn ? auth.getUser()?._id : null;
    const isOwnDeck = currentUserId && deckOwnerId && currentUserId === deckOwnerId;

    return (
      <>
      <Helmet>
          <title>{this.state.name ? this.state.name + " Deck |" : ""} Primal Trading Card Game</title>
          <meta name="description" content="Primal the Trading Card Game is a new TCG for highly skilled TCG players. This is an anime & fantasy art TCG with exciting gameplay and compares to no other existing game.. 1st Ed Release April 2024, join our mailing list to get your first free Primal TCG card and download the Primal TCG app to see all of the available cards. Game designed by Dattebayo Labs LLC ." />
          <meta property="og:title" content="Primal Trading Card Game (TCG) | Awakened Primordials 1st Ed Release April 2024" />
          <meta property="og:description" content="Primal the Trading Card Game is a new TCG for highly skilled TCG players. This is an anime & fantasy art TCG with exciting gameplay and compares to no other existing game.. 1st Ed Release April 2024, join our mailing list to get your first free Primal TCG card and download the Primal TCG app to see all of the available cards. Game designed by Dattebayo Labs LLC ." />
          <meta property="og:image" content="https://pbs.twimg.com/media/GJ38D7mWwAAMXsa?format=jpg&name=medium" />
        </Helmet>
        <Navbar />
        <div className="row bkg-red">
        <div className="col-xs-12 col-sm-10 col-md-10 col-xl-10 offset-sm-1 offset-md-1 offset-xl-1 floating-section">
        {loading ?  <CircularProgress /> : (<div className='row'>
        {fieldCard && (
          <div className="col-xs-12 col-sm-3 col-md-3 col-lg-3 col-xl-3">
            <h2>Field Card</h2>
          {fieldCard.map((card) => (
            <div key={card.id} onClick={() => { this.props.navigate(`/card/${card._id}`)}}>
              <LazyImage
                src={`${constants.DOMAIN}${card.image}`}
                alt={card.name}
                className='mappedCard'
              />
            </div>))}
        </div>
        )}
        <div className='col-9'>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
          <h1 style={{ margin: 0 }}>{name}</h1>
          {!isOwnDeck && (
            <button
              className={`deck-viewer-copy-btn${copyStatus ? ` ${copyStatus}` : ''}`}
              onClick={this.handleCopyDeck}
              disabled={copyStatus === "copying"}
            >
              {this.getCopyButtonText()}
            </button>
          )}
        </div>
        <div>
          <h2>Characters ({`${characters.length}`})</h2>
          {this.sortByTurnCount(characters).map((card) => (
            <div key={card.id} style={{ display: 'inline-block', width: '150px', margin: '10px', cursor: 'pointer' }} onClick={() => { this.props.navigate(`/card/${card._id}`)}}>
              <LazyImage
                src={`${constants.DOMAIN}${card.image}`}
                alt={card.name}
                className="mappedCard"
              />
            </div>
          ))}
        </div>

        <div>
          <h2>Strategies ({`${strategies.length}`})</h2>
          {this.sortByTurnCount(strategies).map((card) => (
            <div key={card.id} style={{ display: 'inline-block', width: '150px', margin: '10px', cursor: 'pointer' }} onClick={() => { this.props.navigate(`/card/${card._id}`)}}>
              <LazyImage
                src={`${constants.DOMAIN}${card.image}`}
                alt={card.name}
                className="mappedCard"
              />
            </div>
          ))}
        </div>

        <div>
          <h2>Abilities ({`${abilities.length}`})</h2>
          {abilities.map((card) => (
            <div key={card.id} style={{ display: 'inline-block', width: '150px', margin: '10px', cursor: 'pointer' }} onClick={() => { this.props.navigate(`/card/${card._id}`)}}>
              <LazyImage
                src={`${constants.DOMAIN}${card.image}`}
                alt={card.name}
                className="mappedCard"
              />
            </div>
          ))}
        </div>
        </div>
        </div>)}



        </div>
        <Footer />
      </div>
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => this.setState({ showLoginModal: false })}
        onLoginSuccess={this.handleLoginSuccess}
        darkMode={false}
      />
      </>

    );
  }

}

export default DeckViewer;
